/**
 * 관리 API 클라이언트 — §10.0 계정 관리.
 *
 * **계약 정본은 서버다**(server/main.py `/api/admin/*`). 인증은 세션 쿠키
 * (`trans_session`, HttpOnly)로 실린다 — 여기서 토큰을 다루지 않는다.
 *
 * 화면이 관리 버튼을 숨기는 것은 **통제가 아니다**. 서버의 `_require_admin`
 * 이 모든 관리 조작의 단일 관문이고, 이 파일은 그 관문에 요청을 보낼 뿐이다.
 * 숨김은 편의(관계없는 사람에게 쓸모없는 버튼을 안 보여 준다)지 보안이 아니다.
 */

import { API_BASE } from './config'
import { ApiError } from './api'

/** 서버 `Account.public()` — 해시는 절대 실리지 않는다. */
export interface AdminAccount {
  username: string
  status: 'pending' | 'approved' | 'disabled'
  is_admin: boolean
  /** §10.0 방 개설 권한 — 승인과 **다른 축**이다 */
  can_create_room: boolean
  /** epoch 초. 0 = 없음 */
  created_at: number
  approved_at: number
  /** 마지막 로그인(0 = 로그인한 적 없음) — "쓰지 않는 계정" 판단 근거 */
  last_login_at: number
}

/** 서버 detail 코드 → 사용자 문구. **코드가 곧 계약이다**(api.ts 와 같은 규율). */
const MESSAGES: Record<string, string> = {
  forbidden: '관리자 권한이 필요합니다.',
  not_found: '대상 계정을 찾을 수 없습니다.',
  // 관리자가 0명이 되면 아무도 승인·권한 부여를 할 수 없다(복구 경로 없음).
  last_admin: '마지막 관리자는 해제할 수 없습니다. 다른 관리자를 먼저 지정해 주세요.',
  exists: '이미 사용 중인 아이디입니다.',
  invalid_username: '아이디 형식이 올바르지 않습니다.',
  weak_password: '비밀번호는 8자 이상이어야 합니다.',
  trivial_password: '너무 쉬운 비밀번호입니다. 다른 값을 써 주세요.',
  unauthenticated: '로그인이 필요합니다.',
}

async function admin<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  const text = await res.text()
  let body: unknown = {}
  try {
    body = text ? JSON.parse(text) : {}
  } catch {
    body = {}
  }
  if (!res.ok) {
    const detail = (body as { detail?: unknown }).detail
    const code = typeof detail === 'string' ? detail : 'unknown'
    throw new ApiError(res.status, code, MESSAGES[code] ?? '요청이 실패했습니다.')
  }
  return body as T
}

export function listAccounts(): Promise<{ accounts: AdminAccount[] }> {
  return admin('/admin/accounts')
}

/** 승인 대기 계정을 승인한다 — 그때부터만 로그인할 수 있다(§10.0). */
export function approveAccount(username: string): Promise<{ ok: true }> {
  return admin(`/admin/approve/${encodeURIComponent(username)}`, { method: 'POST' })
}

export interface NewAccountInput {
  username: string
  password: string
  approved: boolean
  is_admin: boolean
  can_create_room: boolean
}

/**
 * 관리자가 계정을 **직접 만든다** — 가입 대기 없이.
 * 회의 30분 전에 참가자가 정해지는 것이 실사용이다.
 */
export function createAccount(input: NewAccountInput): Promise<{ ok: true; username: string }> {
  return admin('/admin/accounts', { method: 'POST', body: JSON.stringify(input) })
}

/** §10.0 방 개설 권한 부여·회수. */
export function setCanCreateRoom(
  username: string,
  canCreateRoom: boolean,
): Promise<{ ok: true; can_create_room: boolean }> {
  return admin(`/admin/accounts/${encodeURIComponent(username)}/capability`, {
    method: 'POST',
    body: JSON.stringify({ can_create_room: canCreateRoom }),
  })
}

/** 관리자 승격·강등 — 마지막 관리자는 서버가 막는다(409 `last_admin`). */
export function setAdmin(
  username: string,
  isAdmin: boolean,
): Promise<{ ok: true; is_admin: boolean }> {
  return admin(`/admin/accounts/${encodeURIComponent(username)}/admin`, {
    method: 'POST',
    body: JSON.stringify({ is_admin: isAdmin }),
  })
}

/** 비활성화(세션까지 끊긴다) / 재활성화. */
export function setAccountEnabled(
  username: string,
  enabled: boolean,
): Promise<{ ok: true; status: string }> {
  return admin(`/admin/accounts/${encodeURIComponent(username)}/status`, {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  })
}

export function deleteAccount(username: string): Promise<{ ok: true }> {
  return admin(`/admin/accounts/${encodeURIComponent(username)}`, { method: 'DELETE' })
}

/* ── 표시 도우미 ────────────────────────────────────────────────────────── */

export const STATUS_LABELS: Record<AdminAccount['status'], string> = {
  pending: '승인 대기',
  approved: '사용 중',
  disabled: '중지됨',
}

/** epoch 초 → 사람이 읽는 날짜. 0 은 "없음"(지어내지 않는다). */
export function formatStamp(sec: number): string {
  if (!sec) return '—'
  const d = new Date(sec * 1000)
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}
